import { useState } from "react";
import { Form, Link, useRouteLoaderData } from "react-router-dom";

import { AsyncFormSelect } from "../forms/records/form/AsyncFormSelect";
import { SelectOption } from "../forms/types";
import { useAuthStore, User } from "src/components/main/authStore";
import { dateDurationCalc } from "src/components/common/functions";
import { PerDiem } from "./types";

type FormPerDiem = Omit<Partial<PerDiem>, "requester" | "approver"> & {
    requester?: PerDiem["requester"] | SelectOption | null;
    approver?: SelectOption | null;
    project?: SelectOption | null;
};

export default function PerDiemForm({
    keyword,
}: {
    keyword: "Add" | "Edit";
}) {
    const currentUser = useAuthStore(state => state.user) as User;
    const existing = useRouteLoaderData("PerDiem") as { perDiem?: PerDiem } | undefined;
    const requesterDepartment = perDiemRequesterDepartment(existing?.perDiem?.requester ?? null, currentUser.department);
    const [perDiem, setPerDiem] = useState<FormPerDiem>(
        existing?.perDiem
            ? {
                ...existing.perDiem,
                project: existing.perDiem.projectId
                    ? { value: String(existing.perDiem.projectId), label: existing.perDiem.projectName }
                    : null,
            }
            : {
                requester: {
                    label: currentUser.userName,
                    value: currentUser.id,
                    department: currentUser.department,
                    outside: currentUser.outside,
                    projects: currentUser.projects,
                },
                project: null,
                approver: null,
                startDate: "",
                endDate: "",
                hotelName: "",
                hotelAddress: "",
                notes: "",
            }
    );
    const departmentValue = perDiemRequesterDepartment(perDiem.requester ?? null, requesterDepartment);

    return (
        <Form method = "post" className = "my-3">
            {keyword === "Edit" ? <input type = "hidden" name = "id" value = {String(perDiem.id ?? "")} /> : null}
            <input type = "hidden" name = "action" value = {keyword} />
            <div className = "blk5 align-items-center mb-2">
                <div className = "fw-bold me-3 mb-2 required-field">Request Date:</div>
                <div className = "d-flex align-items-center mb-2 me-3">
                    <label className = "me-2" htmlFor = "startDate">From</label>
                    <input
                        required
                        type = "date"
                        min = "1900-01-01"
                        max = {perDiem.endDate || "2999-12-31"}
                        className = "form-control"
                        id = "startDate"
                        name = "startDate"
                        value = {perDiem.startDate ?? ""}
                        onChange = {e => setPerDiem({ ...perDiem, startDate: e.currentTarget.value })}
                    />
                </div>
                <div className = "blk5 align-items-center mb-2">
                    <label className = "me-2" htmlFor = "endDate">To</label>
                    <input
                        required
                        type = "date"
                        min = {perDiem.startDate || "1900-01-01"}
                        max = "2999-12-31"
                        className = "form-control"
                        id = "endDate"
                        name = "endDate"
                        value = {perDiem.endDate ?? ""}
                        onChange = {e => setPerDiem({ ...perDiem, endDate: e.currentTarget.value })}
                    />
                </div>
                {perDiem.startDate && perDiem.endDate ? (
                    <div className = "ms-3">
                        <strong>Duration:</strong> {dateDurationCalc(perDiem.startDate, perDiem.endDate)}
                    </div>
                ) : null}
            </div>
            {currentUser.perDiem === "editAll" ? (
                <div className = "blk5 mb-2 d-flex align-items-center">
                    <label className = "fw-bold me-3 required-field" htmlFor = "requesterId">Requester</label>
                    <div style = {{ minWidth: 200, flexGrow: 1 }}>
                        <AsyncFormSelect
                            placeholder = "Search user…"
                            required = {true}
                            name = "requesterId"
                            inputId = "requesterId"
                            fileName = "common/searchUser.php"
                            value = {perDiem.requester ?? null}
                            onChange = {next => setPerDiem({ ...perDiem, requester: next as SelectOption })}
                        />
                    </div>
                </div>
            ) : (
                <div className = "blk5 mb-2 d-flex align-items-center">
                    <label className = "fw-bold me-3 required-field" htmlFor = "requesterId">Requester</label>
                    <div style = {{ minWidth: 200, flexGrow: 1 }}>
                        <span>{perDiem.requester?.label ?? currentUser.userName}</span>
                        <input type = "hidden" name = "requesterId" value = {perDiem.requester?.value ?? currentUser.id} />
                    </div>
                </div>
            )}
            <div className = "blk5 mb-2 d-flex align-items-center">
                <label className = "fw-bold me-3 required-field text-nowrap" htmlFor = "department">Department</label>
                <input
                    className = "form-control"
                    disabled
                    id = "department"
                    name = "department"
                    value = {departmentValue}
                />
            </div>
            <div className = "blk5 mb-2 d-flex align-items-center">
                <label className = "fw-bold me-3 required-field" htmlFor = "projectId">Project</label>
                <div style = {{ minWidth: 200, flexGrow: 1 }}>
                    <AsyncFormSelect
                        placeholder = "Search project…"
                        required = {true}
                        name = "projectId"
                        inputId = "projectId"
                        fileName = "common/searchProject.php"
                        value = {perDiem.project ?? null}
                        onChange = {next => setPerDiem({ ...perDiem, project: next as SelectOption })}
                    />
                </div>
            </div>
            <div className = "blk5 mb-2 d-flex align-items-center">
                <label className = "fw-bold me-3 required-field" htmlFor = "hotelName">Hotel Name</label>
                <input
                    required
                    id = "hotelName"
                    name = "hotelName"
                    className = "form-control"
                    value = {perDiem.hotelName ?? ""}
                    onChange = {e => setPerDiem({ ...perDiem, hotelName: e.currentTarget.value })}
                />
            </div>
            <div className = "blk5 mb-2 d-flex align-items-center">
                <label className = "fw-bold me-3 required-field" htmlFor = "hotelAddress">Hotel Address</label>
                <input
                    required
                    id = "hotelAddress"
                    name = "hotelAddress"
                    className = "form-control"
                    value = {perDiem.hotelAddress ?? ""}
                    onChange = {e => setPerDiem({ ...perDiem, hotelAddress: e.currentTarget.value })}
                />
            </div>
            <div className = "blk5 mb-2 d-flex align-items-center">
                <label className = "fw-bold me-3 required-field" htmlFor = "approverId">Approver</label>
                <div style = {{ minWidth: 200, flexGrow: 1 }}>
                    <AsyncFormSelect
                        placeholder = "Search user…"
                        required = {true}
                        name = "approverId"
                        inputId = "approverId"
                        fileName = "common/searchUser.php"
                        value = {perDiem.approver ?? null}
                        onChange = {next => setPerDiem({ ...perDiem, approver: next as SelectOption })}
                    />
                </div>
            </div>
            <label className = "mb-2 fw-bold" htmlFor = "notes">Notes</label>
            <textarea
                id = "notes"
                name = "notes"
                value = {perDiem.notes ?? ""}
                onChange = {e => setPerDiem({ ...perDiem, notes: e.currentTarget.value })}
                className = "form-control mb-2"
                style = {{ width: "100%" }}
            />
            <div>
                <button className = "btn btn-primary me-3">{keyword}</button>
                <Link to = ".." className = "btn btn-outline-secondary float-end">Back</Link>
            </div>
        </Form>
    );
}

function perDiemRequesterDepartment(
    requester: FormPerDiem["requester"],
    fallback: string
) {
    if (requester && "department" in requester) {
        return requester.department ?? "";
    }
    return fallback ?? "";
}
